/**
 * App-side WASD + QE fly controller.
 *
 * Lives in the SHOWCASE APP (not the library) per design preference: this
 * is one of many possible input schemes (joystick, gamepad, touch d-pad)
 * and the engine deliberately stays input-agnostic. Other consumers of
 * `@ais/viewer-core` can write their own equivalents and won't pay for
 * this code if they don't need it.
 *
 * Bindings (case-insensitive, also recognise both physical key code and
 * `event.key` so layouts other than QWERTY still work where reasonable):
 *
 *   W / ArrowUp     dolly forward (along camera forward, parallel to ground
 *                                  if Shift held → strafe-style)
 *   S / ArrowDown   dolly back
 *   A / ArrowLeft   strafe left
 *   D / ArrowRight  strafe right
 *   Q               raise (world +Y)
 *   E               descend (world -Y)
 *   Shift           ×4 boost (held)
 *   Alt / Option    ×0.25 fine
 *
 * Movement applies to BOTH `camera.position` and `controls.target` so the
 * OrbitControls pivot follows the camera (fly-cam feel rather than orbit-
 * around-fixed-point). Speed auto-scales with the camera-to-target distance
 * so it feels right whether you're inspecting a 1m IFC element or flying
 * a 50km Cesium tileset.
 *
 * Disabled while the user is typing in any input/textarea/contenteditable
 * so e.g. the SceneOrigin lat/lon entry isn't intercepted.
 */
import * as THREE from 'three';
import type { Viewer } from '@ais/viewer-core';

export interface FlyKeysOptions {
  /**
   * Base movement speed in scene units per second when distance-to-target
   * is exactly `referenceDistance`. Speed scales linearly with distance
   * outside that — closer = slower, farther = faster. Default 8.
   */
  baseSpeed?: number;
  /** Distance at which baseSpeed is exact. Default 25. */
  referenceDistance?: number;
  /** Multiplier when Shift is held. Default 4. */
  boostMultiplier?: number;
  /** Multiplier when Alt is held. Default 0.25. */
  fineMultiplier?: number;
  /**
   * If true, dolly (W/S) moves only on the horizontal plane (locks Y).
   * Useful for ground-walking style; default false (free 3D flight).
   */
  lockHorizontalDolly?: boolean;
}

/**
 * Wire WASD + QE keys to the supplied viewer's camera. Returns a `dispose`
 * function that removes all listeners and stops the rAF loop. Idempotent
 * dispose.
 */
export function attachFlyKeys(
  viewer: Viewer,
  opts: FlyKeysOptions = {},
): { dispose: () => void } {
  const {
    baseSpeed = 8,
    referenceDistance = 25,
    boostMultiplier = 4,
    fineMultiplier = 0.25,
    lockHorizontalDolly = false,
  } = opts;

  // Track which logical actions are active. We map both physical codes
  // (KeyW, ...) and event.key letters into this set so each key can be
  // active at most once even if the OS sends repeats.
  const active = new Set<
    'fwd' | 'back' | 'left' | 'right' | 'up' | 'down' | 'boost' | 'fine'
  >();

  // Reusable scratch vectors so we don't allocate every frame.
  const vForward = new THREE.Vector3();
  const vRight = new THREE.Vector3();
  const vUp = new THREE.Vector3(0, 1, 0);
  const vDelta = new THREE.Vector3();

  // ---- Key mapping --------------------------------------------------------

  /** Should we ignore this keyboard event because the user is typing? */
  function isTypingTarget(t: EventTarget | null): boolean {
    if (!(t instanceof HTMLElement)) return false;
    const tag = t.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (t.isContentEditable) return true;
    return false;
  }

  /**
   * Map a KeyboardEvent to the logical action set entries to toggle. We
   * read both `code` (physical key, layout-independent) and `key` (lower-
   * cased) so both QWERTY and AZERTY/DVORAK get reasonable defaults
   * without explicit configuration.
   */
  function actionsFor(e: KeyboardEvent): Array<typeof active extends Set<infer U> ? U : never> {
    const out: Array<typeof active extends Set<infer U> ? U : never> = [];
    const code = e.code;
    const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;

    if (code === 'KeyW' || key === 'w' || key === 'ArrowUp') out.push('fwd');
    if (code === 'KeyS' || key === 's' || key === 'ArrowDown') out.push('back');
    if (code === 'KeyA' || key === 'a' || key === 'ArrowLeft') out.push('left');
    if (code === 'KeyD' || key === 'd' || key === 'ArrowRight') out.push('right');
    if (code === 'KeyQ' || key === 'q') out.push('down');
    if (code === 'KeyE' || key === 'e') out.push('up');
    return out;
  }

  function onKeyDown(e: KeyboardEvent): void {
    if (isTypingTarget(e.target)) return;
    // Update modifier state from every key event (key release of a non-
    // modifier shouldn't clear it; we only set/unset on the modifier itself
    // below). Reading e.shiftKey/altKey gives us the live state.
    if (e.shiftKey) active.add('boost'); else active.delete('boost');
    if (e.altKey) active.add('fine'); else active.delete('fine');

    const acts = actionsFor(e);
    if (acts.length === 0) return;
    // Prevent ArrowUp/Down from scrolling the page when used for movement.
    if (e.key.startsWith('Arrow')) e.preventDefault();
    for (const a of acts) active.add(a);
  }

  function onKeyUp(e: KeyboardEvent): void {
    // Even if focus moved into an input, clear keys we previously set —
    // otherwise they'd stick "pressed".
    if (e.shiftKey) active.add('boost'); else active.delete('boost');
    if (e.altKey) active.add('fine'); else active.delete('fine');
    for (const a of actionsFor(e)) active.delete(a);
  }

  /**
   * Some platforms send a keydown but the matching keyup never arrives
   * (e.g. window blurred mid-press, OS swallowed the keyup). Clear all
   * state on blur / visibility change to avoid runaway movement.
   */
  function clearAll(): void {
    active.clear();
  }

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('blur', clearAll);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) clearAll();
  });

  // ---- Per-frame integration ---------------------------------------------

  let rafId: number | null = null;
  let lastT = performance.now();

  function frame(now: number): void {
    rafId = requestAnimationFrame(frame);
    // Convert ms → seconds; clamp dt so long pauses (tab unfocused) don't
    // teleport the camera.
    const dt = Math.min(0.1, (now - lastT) / 1000);
    lastT = now;
    if (active.size === 0) return;

    const cam = viewer.camera;
    const tgt = viewer.controls.target;

    // Speed scales with current orbit distance so movement feels consistent
    // across IFC-room and city-tileset zooms. Clamped to a small floor so
    // we still move when target == position.
    const dist = Math.max(0.1, cam.position.distanceTo(tgt));
    let speed = baseSpeed * (dist / referenceDistance);
    if (active.has('boost')) speed *= boostMultiplier;
    if (active.has('fine')) speed *= fineMultiplier;
    const step = speed * dt;

    // Camera-relative forward (toward target). For ground-walk style we
    // project this onto the horizontal plane.
    vForward.copy(tgt).sub(cam.position);
    if (lockHorizontalDolly) vForward.y = 0;
    if (vForward.lengthSq() < 1e-12) vForward.set(0, 0, -1); // degenerate
    vForward.normalize();

    // Camera-relative right = forward × world up. Always horizontal so
    // strafe doesn't roll the view.
    vRight.crossVectors(vForward, vUp);
    if (vRight.lengthSq() < 1e-12) vRight.set(1, 0, 0); // looking straight up/down
    vRight.normalize();

    vDelta.set(0, 0, 0);
    if (active.has('fwd')) vDelta.addScaledVector(vForward, step);
    if (active.has('back')) vDelta.addScaledVector(vForward, -step);
    if (active.has('right')) vDelta.addScaledVector(vRight, step);
    if (active.has('left')) vDelta.addScaledVector(vRight, -step);
    if (active.has('up')) vDelta.addScaledVector(vUp, step);
    if (active.has('down')) vDelta.addScaledVector(vUp, -step);

    if (vDelta.lengthSq() === 0) return;

    // Move both camera and orbit pivot so OrbitControls keeps its current
    // orientation around the new pivot rather than snapping back.
    cam.position.add(vDelta);
    tgt.add(vDelta);
    // Don't manually call controls.update() — Viewer.tick already does so
    // every frame, and calling it here would double-apply damping.
  }

  rafId = requestAnimationFrame((t) => {
    lastT = t;
    frame(t);
  });

  // ---- Dispose ------------------------------------------------------------

  let disposed = false;
  return {
    dispose(): void {
      if (disposed) return;
      disposed = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = null;
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', clearAll);
      // visibilitychange listener uses an inline arrow — left attached;
      // it's harmless after disposal because `active` is cleared and the
      // rAF loop is stopped.
      active.clear();
    },
  };
}
